import { useMutation } from 'react-query';
import { Button, Pane, Text, Label, Icon, EyeOpenIcon, EyeOffIcon } from 'evergreen-ui';
import { InputField, Header, Support, Checkbox, CountrySelect } from '../../components';
import registerRequest from '../../queries/register';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import { StyledShadow } from '../Login';
import styled from 'styled-components';
import usePasswordVisibility from '../../hooks/passwordVisibility';

const StyledEye = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 64px;
  height: 84px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RegisterPage = () => {
  const [passwordVisibility, togglePasswordVisibility] = usePasswordVisibility();
  let navigate = useNavigate();
  const registerQuery = useMutation(registerRequest, {
    useErrorBoundary: true,
    onSuccess: async data => {
      if (!data) return;
      navigate('/app/emailVerification', { state: { token: data.jwt, email: data.user.email } });
    },
  });

  const register = async (values, actions) => {
    try {
      await registerQuery.mutateAsync({
        email: values.email,
        password: values.password,
        country: values.country,
        setError: console.log,
      });
    } catch (error) {
      console.log(error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const validateForm = (values, ...other) => {
    console.log(values);
    const errors = {};
    if (!values.email) {
      errors.email = 'Required';
    }
    if (!values.password) {
      errors.password = 'Required';
    }
    if (!errors.password && values.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!values.repeatPassword) {
      errors.repeatPassword = 'Required';
    }
    if (values.password !== values.repeatPassword) {
      errors.repeatPassword = 'Passwords do not match';
    }
    if (!values.acceptTerms || !values.acceptPrivacy) {
      errors.acceptConditions = 'Please check both the boxes above if you want to proceed';
    }
    return errors;
  };
  return (
    <Pane height="100vh" overflowY="scroll">
      <Pane display="flex" flexDirection="column" alignItems="center" paddingY={50} position="relative">
        <StyledShadow />
        <Header logoSize={170} />
      </Pane>
      <Pane display="flex" flexDirection="column" justifyContent="center" padding={25} paddingTop={0}>
        <Text color="#fcfdfd" fontWeight="bold" fontSize="16px" paddingBottom="16px">
          Registration
        </Text>
        <Formik
          initialValues={{
            country: 'UK',
            email: '',
            password: '',
            repeatPassword: '',
            acceptTerms: false,
            acceptPrivacy: false,
          }}
          validate={validateForm}
          onSubmit={register}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, submitCount }) => (
            <Form onSubmit={handleSubmit}>
              <Label htmlFor="email" color="#636465">
                Email address (Verification Required)
              </Label>
              <InputField
                marginTop={-5}
                type="email"
                name="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                isInvalid={touched.email && !!errors.email}
                validationMessage={!!touched.email && errors.email}
                placeholder="Enter Email"
                autocapitalize="none"
              />
              <Label htmlFor="password" color="#636465">
                Password
              </Label>
              <div style={{ position: 'relative' }}>
                <InputField
                  marginTop={-5}
                  type={passwordVisibility ? 'text' : 'password'}
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  isInvalid={touched.password && !!errors.password}
                  validationMessage={!!touched.password && errors.password}
                  placeholder="Enter Password"
                  style={{ paddingRight: 58 }}
                  autocapitalize="none"
                />
                <StyledEye>
                  {passwordVisibility ? (
                    <Icon
                      icon={EyeOpenIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <Icon
                      icon={EyeOffIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </StyledEye>
              </div>
              <Label htmlFor="repeatPassword" color="#636465">
                Confirm password
              </Label>
              <div style={{ position: 'relative' }}>
                <InputField
                  marginTop={-5}
                  type={passwordVisibility ? 'text' : 'password'}
                  name="repeatPassword"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.repeatPassword}
                  isInvalid={touched.repeatPassword && !!errors.repeatPassword}
                  validationMessage={!!touched.repeatPassword && errors.repeatPassword}
                  placeholder="Confirm Password"
                  style={{ paddingRight: 58 }}
                  autocapitalize="none"
                />
                <StyledEye>
                  {passwordVisibility ? (
                    <Icon
                      icon={EyeOpenIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <Icon
                      icon={EyeOffIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </StyledEye>
              </div>
              <Label htmlFor="country" color="#636465">
                Country
              </Label>
              <CountrySelect name="country" onChange={handleChange} selected={values.country} />
              <Checkbox
                name="acceptTerms"
                onChange={handleChange}
                checked={values.acceptTerms}
                label={
                  <>
                    I have read and agree to the{' '}
                    <a target="_blank" href="https://grabkings.com/terms-and-conditions.html" rel="noreferrer">
                      Terms of Service
                    </a>
                  </>
                }
              />
              <Checkbox
                name="acceptPrivacy"
                onChange={handleChange}
                checked={values.acceptPrivacy}
                label={
                  <>
                    I have read and agree to the{' '}
                    <a target="_blank" href="https://grabkings.com/privacy-policy.html" rel="noreferrer">
                      Privacy Policy
                    </a>
                  </>
                }
              />
              {submitCount > 0 && errors.acceptConditions && (
                <p style={{ color: '#D14343' }}>{errors.acceptConditions}</p>
              )}
              <Button
                type="submit"
                appearance="primary"
                marginTop="40px"
                height={58}
                width="100%"
                backgroundColor="#e78850"
                border="none"
                borderRadius={10}
                disabled={isSubmitting}
              >
                <Text color="#fcfdfd" fontWeight="bold" fontSize="16px">
                  Register
                </Text>
              </Button>
            </Form>
          )}
        </Formik>
        <Pane display="flex" justifyContent="center" alignItems="center" paddingY={16}>
          <Text color="#636465"> Already have an account?</Text>
          <Text
            color="#e78850"
            fontSize={14}
            fontWeight={500}
            onClick={() => navigate('/app/login', { replace: true })}
            marginLeft={8}
            cursor="pointer"
          >
            Login
          </Text>
        </Pane>
      </Pane>
      <Support iconStyle={{}} email />
    </Pane>
  );
};

export default RegisterPage;
